import { useId, useState, type ReactNode } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Info } from "lucide-react";
import { DUR, EASE_ENTER } from "../motion/tokens";

/**
 * A small ⓘ that explains something on hover, keyboard focus or tap (phones have no hover).
 * A click focuses first, so the click only opens; blur (tapping elsewhere) or Escape closes.
 */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span className="relative inline-flex" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" aria-label={label} aria-describedby={open ? id : undefined} aria-expanded={open}
        onClick={() => setOpen(true)} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
        className="grid h-6 w-6 place-items-center rounded-full text-[var(--color-text-muted)] hover:text-[var(--color-primary)] focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]">
        <Info className="h-4 w-4" aria-hidden />
      </button>
      <AnimatePresence>
        {open && (
          <m.span id={id} role="tooltip"
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0, transition: { duration: DUR.micro, ease: EASE_ENTER } }} exit={{ opacity: 0 }}
            className="absolute left-1/2 top-8 z-30 w-72 max-w-[80vw] -translate-x-1/2 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3 text-sm text-[var(--color-text)] shadow-lg">
            {children}
          </m.span>
        )}
      </AnimatePresence>
    </span>
  );
}
